/**
 * Camera-status donut for a node's summary cards — the cloud's per-site row and the local
 * node's own dashboard render the same ring from the same camera list.
 *
 * COLOR JOB: status (state), not identity — so it uses the project's reserved
 * status tokens (--success / --warning / --destructive / --muted-foreground)
 * rather than a categorical ramp, and every slice ships with a label + count.
 *
 * ACCESSIBILITY: those tokens collapse under protanopia — success #22c55e vs
 * warning #eab308 measure ΔE 4.2, below the ΔE 6 floor, in BOTH light and dark
 * (the theme doesn't restyle them for dark). Hue therefore cannot be the only
 * channel here, which is why the count list beside the ring is always rendered
 * rather than hidden behind hover: the legend, not the ring, is the instrument
 * that carries the values.
 *
 * Slices are separated by a 2px gap in the surface color (no strokes on marks).
 */

export interface DonutSlice {
  key: string;
  label: string;
  value: number;
  /** CSS color — a project status token, e.g. `var(--success)`. */
  color: string;
}

interface CameraStatusDonutProps {
  slices: DonutSlice[];
  /** Rendered in the ring's hole. */
  total: number;
  size?: number;
}

/** 2px of surface between slices, expressed as a stroke-dash gap in px of arc. */
const GAP_PX = 2;
const STROKE = 9;
/**
 * Smallest arc a non-zero slice may render as.
 *
 * Without this, a slice whose arc is shorter than GAP_PX gets subtracted to zero
 * and disappears: 1 error camera out of 200 is a 1.49px arc on a 298px ring, so
 * the one status an operator must not miss would render as nothing. A slice at
 * this floor gives up its gap first and then over-draws its neighbour's tail by
 * a fraction of a pixel — visibly present beats geometrically exact.
 */
const MIN_INK_PX = 2;

export default function CameraStatusDonut({ slices, total, size = 84 }: CameraStatusDonutProps) {
  const present = slices.filter((s) => s.value > 0);
  const radius = (size - STROKE) / 2;
  const circumference = 2 * Math.PI * radius;

  // A single non-zero status is a full ring — a gap would render as a stray
  // notch in an otherwise continuous circle, so drop it in that case.
  const gap = present.length > 1 ? GAP_PX : 0;

  let offset = 0;
  const arcs = present.map((s) => {
    const fraction = s.value / total;
    const raw = circumference * fraction;
    // A slice too small to afford the full gap forfeits the gap before it
    // forfeits its ink; MIN_INK_PX is the floor below which it can't shrink.
    const usableGap = Math.min(gap, Math.max(0, raw - MIN_INK_PX));
    const length = Math.max(MIN_INK_PX, raw - usableGap);
    const arc = { ...s, length, offset, fraction };
    // Offsets advance by the TRUE fraction so the slices still close the circle.
    offset += raw;
    return arc;
  });

  return (
    <div className="flex items-center gap-3">
      <div className="relative flex-none" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={`${total} cameras by status`}>
          {/* Track — an all-zero node must still read as a ring. Mixed off the ink
              colour rather than `--muted`, which is nearly indistinguishable from the
              recessed tile surface it sits on in light mode. */}
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="color-mix(in srgb, var(--muted-foreground) 20%, transparent)"
            strokeWidth={STROKE}
          />
          {/* -90deg so the first slice starts at 12 o'clock. */}
          <g transform={`rotate(-90 ${size / 2} ${size / 2})`}>
            {arcs.map((a) => (
              <circle
                key={a.key}
                cx={size / 2}
                cy={size / 2}
                r={radius}
                fill="none"
                stroke={a.color}
                strokeWidth={STROKE}
                strokeDasharray={`${a.length} ${circumference - a.length}`}
                strokeDashoffset={-a.offset}
                strokeLinecap="butt"
              >
                <title>{`${a.label}: ${a.value} (${Math.round(a.fraction * 100)}%)`}</title>
              </circle>
            ))}
          </g>
        </svg>
        {/* Hero number in the hole — text wears ink tokens, never a slice color. */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-[17px] font-bold leading-none tabular-nums text-foreground">{total}</span>
          <span className="mt-0.5 text-[8px] uppercase tracking-wider text-muted-foreground">cams</span>
        </div>
      </div>

      {/* Always-visible counts — the secondary channel that makes the ring safe. */}
      <ul className="min-w-0 flex-1 space-y-1">
        {slices.map((s) => (
          <li key={s.key} className="flex items-center gap-1.5 text-[10px] leading-tight">
            <span className="h-1.5 w-1.5 flex-none rounded-full" style={{ backgroundColor: s.color }} aria-hidden />
            <span className="min-w-0 flex-1 truncate text-muted-foreground">{s.label}</span>
            <span className="flex-none font-semibold tabular-nums text-foreground">{s.value}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
